"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import { Newsreader, DM_Sans, DM_Mono, DM_Serif_Display } from "next/font/google";
import { SignUpButton, SignInButton } from "@clerk/nextjs";
import LandingFooter from "@/components/LandingFooter";

// Loaded via next/font so the fonts are self-hosted, preloaded, and non-render-blocking
// (previously this was a synchronous @import inside a <style> tag, which delays FCP/LCP).
const mlSerif = Newsreader({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  style: ["normal", "italic"],
  display: "swap",
  variable: "--ml-font-serif",
});
const mlSans = DM_Sans({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
  display: "swap",
  variable: "--ml-font-sans",
});
const mlMono = DM_Mono({
  subsets: ["latin"],
  weight: ["400", "500"],
  display: "swap",
  variable: "--ml-font-mono",
});
const mlSerifDisplay = DM_Serif_Display({
  subsets: ["latin"],
  weight: "400",
  style: ["normal", "italic"],
  display: "swap",
  variable: "--ml-font-serif-display",
});
const mlFontClass = `${mlSerif.variable} ${mlSans.variable} ${mlMono.variable} ${mlSerifDisplay.variable}`;

/**
 * Mobile-only landing page.
 *
 * A faithful port of the "Svendetid Mobile Landing" Claude Design page. It is
 * rendered in place of the desktop landing on small viewports (see page.tsx).
 * The desktop landing is left completely untouched.
 *
 * All design tokens used here are inlined under the .svd-mobile scope so this
 * component does not depend on the external design-system token files.
 */

type Tile = { label: string; icon: string; color: string; badge?: string; href: string };
type Feature = {
  eyebrow: string;
  title: string;
  body: string;
  link: string;
  href: string;
  icon: string;
  tint: string;
  iconColor: string;
};

const ML_T = {
  da: {
    navFeaturesLabel: "Funktioner",
    navSvendAi: "Svend AI",
    navSvendAiHref: "/ai",
    navPricing: "Priser",
    navPricingHref: "/pricing",
    navFeatures: [
      { icon: "⚡", label: "Automatisering", href: "/features/automation" },
      { icon: "🧾", label: "Fakturascanning", href: "/features/invoice-scanning" },
      { icon: "📅", label: "Kalender", href: "/features/calendar" },
      { icon: "⏱", label: "Tidsregistrering", href: "/features/time-registration" },
      { icon: "📸", label: "Kvalitetsdokumentation", href: "/features/quality-control" },
      { icon: "📄", label: "Tilbud", href: "/features/offer" },
      { icon: "📊", label: "Rapporter", href: "/features/reports" },
      { icon: "➕", label: "Se alle funktioner", href: "/features" },
    ],
    getStarted: "Kom i gang",
    signIn: "Log ind",
    heroBadge: "Jobstyring for håndværkere",
    heroH1a: "Én app til",
    heroH1b: "hele ",
    heroH1em: "håndværket",
    heroSub: "Tilbud, jobs, tidsregistrering, lager og fakturering — samlet ét sted. Fra første henvendelse til sidste faktura.",
    heroCta: "Kom i gang gratis",
    tilesTop: [
      { label: "Automation", icon: "flash", color: "#2563C9", href: "/features/automation" },
      { label: "Faktura­scanning", icon: "scan", color: "#2F8F5B", href: "/features/invoice-scanning" },
      { label: "Kalender", icon: "calendar-clear", color: "#E08A1E", href: "/features/calendar" },
      { label: "Tids­registrering", icon: "stopwatch", color: "#2F6B48", href: "/features/time-registration" },
      { label: "Dokumen­tation", icon: "document-text", color: "#C2483B", href: "/features/quality-control" },
    ],
    tilesBottom: [
      { label: "Tilbud", icon: "pricetags", color: "#F97316", href: "/features/offer" },
      { label: "Rapporter", icon: "bar-chart", color: "#6B4FA0", href: "/features/reports" },
      { label: "og mere…", icon: "apps", color: "#2A8C8C", href: "/features" },
    ],
    svendAiTileBadge: "Ny",
    featuresEyebrow: "Alt på ét sted",
    featuresH2a: "Slut med ti apps og ",
    featuresH2em: "løse",
    featuresH2b: " sedler.",
    features: [
      { eyebrow: "Tilbud & ordrer", title: "Send tilbud, mens du står hos kunden.", body: "Byg professionelle tilbud på minutter med priser fra dit eget katalog. Kunden godkender med ét tryk.", link: "Se tilbud", href: "/features/offer", icon: "document-text", tint: "#FDE2C8", iconColor: "#C2410C" },
      { eyebrow: "Tidsregistrering", title: "Stempl ind og ud direkte fra pladsen.", body: "Registrér timer på det rigtige job med ét tryk. Alt lander automatisk på fakturaen.", link: "Se tidsregistrering", href: "/features/time-registration", icon: "time", tint: "#D4F0DC", iconColor: "#2F6B48" },
      { eyebrow: "Fakturering", title: "Fra udført job til betalt faktura.", body: "Lav fakturaen ud fra timer og materialer, og send den direkte fra appen. Følg hvem der har betalt.", link: "Se funktioner", href: "/features", icon: "receipt", tint: "#E5E1F7", iconColor: "#5B4B8A" },
      { eyebrow: "Lager & leverandører", title: "Hold styr på materialer og indkøb.", body: "Se hvad du har på lager, hvad et job har kostet, og bestil hos dine leverandører — uden papir.", link: "Se funktioner", href: "/features", icon: "cube", tint: "#CCFBF1", iconColor: "#1C5138" },
    ],
    heroNote: "14 dages gratis · Intet kort · Opsig når som helst",
    ctaH2a: "Klar til at samle",
    ctaH2b: "hele firmaet?",
    ctaSub: "Kom i gang på under to minutter. Tag dit første job med i dag.",
    ctaBtn: "Opret gratis konto",
  },
  en: {
    navFeaturesLabel: "Features",
    navSvendAi: "Svend AI",
    navSvendAiHref: "/en/ai",
    navPricing: "Pricing",
    navPricingHref: "/en/pricing",
    navFeatures: [
      { icon: "⚡", label: "Automation", href: "/en/features/automation" },
      { icon: "🧾", label: "Invoice scanning", href: "/en/features/invoice-scanning" },
      { icon: "📅", label: "Calendar", href: "/en/features/calendar" },
      { icon: "⏱", label: "Time tracking", href: "/en/features/time-registration" },
      { icon: "📸", label: "QC documentation", href: "/en/features/quality-control" },
      { icon: "📄", label: "Quotes", href: "/en/features/offer" },
      { icon: "📊", label: "Reports", href: "/en/features/reports" },
      { icon: "➕", label: "See all features", href: "/en/features" },
    ],
    getStarted: "Get started",
    signIn: "Sign in",
    heroBadge: "Job management for tradespeople",
    heroH1a: "One app for",
    heroH1b: "the whole ",
    heroH1em: "trade",
    heroSub: "Quotes, jobs, time tracking, inventory and invoicing — all in one place. From first enquiry to final invoice.",
    heroCta: "Get started free",
    heroNote: "14 days free · No card · Cancel anytime",
    tilesTop: [
      { label: "Automation", icon: "flash", color: "#2563C9", href: "/en/features/automation" },
      { label: "Invoice scan", icon: "scan", color: "#2F8F5B", href: "/en/features/invoice-scanning" },
      { label: "Calendar", icon: "calendar-clear", color: "#E08A1E", href: "/en/features/calendar" },
      { label: "Time tracking", icon: "stopwatch", color: "#2F6B48", href: "/en/features/time-registration" },
      { label: "Documen­tation", icon: "document-text", color: "#C2483B", href: "/en/features/quality-control" },
    ],
    tilesBottom: [
      { label: "Quotes", icon: "pricetags", color: "#F97316", href: "/en/features/offer" },
      { label: "Reports", icon: "bar-chart", color: "#6B4FA0", href: "/en/features/reports" },
      { label: "and more…", icon: "apps", color: "#2A8C8C", href: "/en/features" },
    ],
    svendAiTileBadge: "New",
    featuresEyebrow: "All in one place",
    featuresH2a: "No more ten apps and ",
    featuresH2em: "loose",
    featuresH2b: " slips.",
    features: [
      { eyebrow: "Quotes & orders", title: "Send quotes while you're with the customer.", body: "Build professional quotes in minutes with prices from your own catalogue. Customer approves with one tap.", link: "See quotes", href: "/en/features/offer", icon: "document-text", tint: "#FDE2C8", iconColor: "#C2410C" },
      { eyebrow: "Time tracking", title: "Clock in and out directly from the site.", body: "Log hours on the right job with one tap. Everything lands automatically on the invoice.", link: "See time tracking", href: "/en/features/time-registration", icon: "time", tint: "#D4F0DC", iconColor: "#2F6B48" },
      { eyebrow: "Invoicing", title: "From completed job to paid invoice.", body: "Create the invoice from hours and materials, and send it directly from the app. Track who has paid.", link: "See features", href: "/en/features", icon: "receipt", tint: "#E5E1F7", iconColor: "#5B4B8A" },
      { eyebrow: "Stock & suppliers", title: "Keep track of materials and purchases.", body: "See what you have in stock, what a job cost, and order from your suppliers — no paperwork.", link: "See features", href: "/en/features", icon: "cube", tint: "#CCFBF1", iconColor: "#1C5138" },
    ],
    ctaH2a: "Ready to bring",
    ctaH2b: "the whole company together?",
    ctaSub: "Get started in under two minutes. Take your first job today.",
    ctaBtn: "Create free account",
  },
};

/** Inline ionicon-style SVGs keyed by name (outline variants). */
function Icon({ name, size, color }: { name: string; size: number; color?: string }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 512 512",
    fill: "none" as const,
    stroke: color ?? "currentColor",
    style: { color: color ?? "currentColor", display: "block" },
  };
  const sw = 32;
  switch (name) {
    case "menu":
      return (
        <svg {...common} stroke={color ?? "currentColor"}>
          <line x1="88" y1="152" x2="424" y2="152" strokeWidth={sw} strokeLinecap="round" />
          <line x1="88" y1="256" x2="424" y2="256" strokeWidth={sw} strokeLinecap="round" />
          <line x1="88" y1="360" x2="424" y2="360" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "close":
      return (
        <svg {...common}>
          <line x1="368" y1="368" x2="144" y2="144" strokeWidth={sw} strokeLinecap="round" />
          <line x1="368" y1="144" x2="144" y2="368" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "chevron-forward":
      return (
        <svg {...common}>
          <polyline points="184 112 328 256 184 400" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "arrow-forward":
      return (
        <svg {...common}>
          <line x1="64" y1="256" x2="448" y2="256" strokeWidth={sw} strokeLinecap="round" />
          <polyline points="320 128 448 256 320 384" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "checkmark-circle":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <path d="M256 48C141.31 48 48 141.31 48 256s93.31 208 208 208 208-93.31 208-208S370.69 48 256 48zm108.25 138.29l-134.4 160a16 16 0 01-12 5.71h-.27a16 16 0 01-11.89-5.3l-57.6-64a16 16 0 1123.78-21.4l45.29 50.32 122.59-145.91a16 16 0 0124.5 20.58z" />
        </svg>
      );
    case "ellipse":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <circle cx="256" cy="256" r="192" />
        </svg>
      );
    case "star":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <path d="M394 480a16 16 0 01-9.39-3L256 383.76 127.39 477a16 16 0 01-24.55-18.08L153 310.35 23 221.2a16 16 0 019-29.2h160.38l48.4-148.95a16 16 0 0130.44 0l48.4 149H480a16 16 0 019.05 29.2L359 310.35l50.13 148.53A16 16 0 01394 480z" />
        </svg>
      );
    case "flash":
      return (
        <svg {...common}>
          <path d="M315.27 33L96 304h128l-31.51 173.34a1 1 0 001.81.68L416 208H288l31.66-174.21a1 1 0 00-1.79-.79z" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "scan":
      return (
        <svg {...common}>
          <path d="M342 444h46a56 56 0 0056-56v-46M170 444h-46a56 56 0 01-56-56v-46M342 68h46a56 56 0 0156 56v46M170 68h-46a56 56 0 00-56 56v46" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1="32" y1="256" x2="480" y2="256" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "calendar-clear":
      return (
        <svg {...common}>
          <rect x="48" y="80" width="416" height="384" rx="48" strokeWidth={sw} strokeLinejoin="round" />
          <line x1="128" y1="48" x2="128" y2="80" strokeWidth={sw} strokeLinecap="round" />
          <line x1="384" y1="48" x2="384" y2="80" strokeWidth={sw} strokeLinecap="round" />
          <line x1="48" y1="160" x2="464" y2="160" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "stopwatch":
      return (
        <svg {...common}>
          <path d="M256 232l-64 64" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="256" cy="272" r="160" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1="256" y1="112" x2="256" y2="80" strokeWidth={sw} strokeLinecap="round" />
          <line x1="208" y1="48" x2="304" y2="48" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "document-text":
      return (
        <svg {...common}>
          <path d="M416 221.25V416a48 48 0 01-48 48H144a48 48 0 01-48-48V96a48 48 0 0148-48h98.75a32 32 0 0122.62 9.37l141.26 141.26a32 32 0 019.37 22.62z" strokeWidth={sw} strokeLinejoin="round" />
          <path d="M256 56v120a32 32 0 0032 32h120M176 288h160M176 368h160" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "pricetags":
      return (
        <svg {...common}>
          <path d="M403.29 32H280.36a14.46 14.46 0 00-10.2 4.22L24.4 281.94a28.85 28.85 0 000 40.78l134.88 134.88a28.85 28.85 0 0040.78 0L445.78 241.84a14.46 14.46 0 004.22-10.2V78.71A46.71 46.71 0 00403.29 32z" strokeWidth={sw} strokeLinejoin="round" />
          <circle cx="352" cy="128" r="32" fill={color ?? "currentColor"} stroke="none" />
        </svg>
      );
    case "bar-chart":
      return (
        <svg {...common}>
          <line x1="32" y1="32" x2="32" y2="480" strokeWidth={sw} strokeLinecap="round" />
          <rect x="96" y="288" width="80" height="160" rx="12" strokeWidth={sw} strokeLinejoin="round" />
          <rect x="224" y="160" width="80" height="288" rx="12" strokeWidth={sw} strokeLinejoin="round" />
          <rect x="352" y="64" width="80" height="384" rx="12" strokeWidth={sw} strokeLinejoin="round" />
        </svg>
      );
    case "apps":
      return (
        <svg {...common}>
          {[104, 256, 408].map((cy) =>
            [104, 256, 408].map((cx) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="40" strokeWidth={sw} />
            ))
          )}
        </svg>
      );
    case "time":
      return (
        <svg {...common}>
          <path d="M256 64C150 64 64 150 64 256s86 192 192 192 192-86 192-192S362 64 256 64z" strokeWidth={sw} strokeLinejoin="round" />
          <polyline points="256 128 256 272 352 272" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "receipt":
      return (
        <svg {...common}>
          <path d="M422 96L380.48 60.79a12 12 0 00-15.5.32L336 86.83 307 60.92a12 12 0 00-16.08 0L262 86.83l-29-25.91a12 12 0 00-16.08 0l-29 25.91-28.92-25.83a12 12 0 00-15.5-.32L96 96v336l45.52-35.21a12 12 0 0115.5.32L186 422.91a12 12 0 0016.08 0L231 397l29 25.91a12 12 0 0016.08 0L305 397l28.92 25.83a12 12 0 0015.5-.32L380 397v0L422 432z" strokeWidth={sw} strokeLinejoin="round" />
          <line x1="176" y1="208" x2="336" y2="208" strokeWidth={sw} strokeLinecap="round" />
          <line x1="176" y1="288" x2="336" y2="288" strokeWidth={sw} strokeLinecap="round" />
        </svg>
      );
    case "sparkle":
      return (
        <svg {...common}>
          <path d="M256 48l35 129 129 35-129 35-35 129-35-129-129-35 129-35z" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <path d="M403 358l14 50 50 14-50 14-14 50-14-50-50-14 50-14z" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "cube":
      return (
        <svg {...common}>
          <path d="M448 341.37V170.61A32 32 0 00432.11 143l-152-88.46a47.94 47.94 0 00-48.24 0L79.89 143A32 32 0 0064 170.61v170.76A32 32 0 0079.89 369l152 88.46a48 48 0 0048.24 0l152-88.46A32 32 0 00448 341.37z" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <polyline points="69 153.99 256 263.99 443 153.99" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
          <line x1="256" y1="463.99" x2="256" y2="263.99" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "logo-linkedin":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <path d="M444.17 32H70.28C49.85 32 32 46.7 32 66.89v374.72C32 461.91 49.85 480 70.28 480h373.78c20.54 0 35.94-18.21 35.94-38.39V66.89C480.12 46.7 464.6 32 444.17 32zm-273.3 373.43h-64.18V205.88h64.18zM141 175.54h-.46c-20.54 0-33.84-15.29-33.84-34.43 0-19.49 13.65-34.42 34.65-34.42s33.85 14.82 34.31 34.42c0 19.14-13.3 34.43-34.66 34.43zm264.43 229.89h-64.18V296.32c0-26.14-9.34-44-32.56-44-17.74 0-28.24 12-32.91 23.69-1.75 4.2-2.22 9.92-2.22 15.76v113.66h-64.18V205.88h64.18v27.77c9.34-13.3 23.93-32.44 57.88-32.44 42.13 0 74 27.77 74 87.64z" />
        </svg>
      );
    case "logo-facebook":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <path d="M480 257.35c0-123.7-100.3-224-224-224s-224 100.3-224 224c0 111.8 81.9 204.47 189 221.29V322.12h-56.89v-64.77H221V208c0-56.13 33.45-87.16 84.61-87.16 24.51 0 50.15 4.38 50.15 4.38v55.13h-28.26c-27.81 0-36.51 17.26-36.51 35v42h62.12l-9.92 64.77H291v156.54c107.1-16.81 189-109.48 189-221.31z" />
        </svg>
      );
    case "logo-instagram":
      return (
        <svg viewBox="0 0 512 512" width={size} height={size} fill={color ?? "currentColor"} style={{ display: "block" }}>
          <path d="M349.33 69.33a93.62 93.62 0 0193.34 93.34v186.66a93.62 93.62 0 01-93.34 93.34H162.67a93.62 93.62 0 01-93.34-93.34V162.67a93.62 93.62 0 0193.34-93.34h186.66m0-37.33H162.67C90.8 32 32 90.8 32 162.67v186.66C32 421.2 90.8 480 162.67 480h186.66C421.2 480 480 421.2 480 349.33V162.67C480 90.8 421.2 32 349.33 32z" />
          <path d="M377.33 162.67a28 28 0 1128-28 27.94 27.94 0 01-28 28zM256 181.33A74.67 74.67 0 11181.33 256 74.75 74.75 0 01256 181.33m0-37.33a112 112 0 10112 112 112 112 0 00-112-112z" />
        </svg>
      );
    default:
      return null;
  }
}

export default function MobileLanding({ lang = 'da' }: { lang?: 'da' | 'en' }) {
  const t = ML_T[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);

  const TilesRow = ({ items }: { items: Tile[] }) =>
    items.map((tile) => (
      <a key={tile.label} href={tile.href} className="svd-tile" style={{ textDecoration: "none" }}>
        <div className="svd-tile-badge-slot">
          {tile.badge ? <span className="svd-tile-badge">{tile.badge}</span> : null}
        </div>
        <div className="svd-tile-icon">
          <Icon name={tile.icon} size={34} color={tile.color} />
        </div>
        <div className="svd-tile-label">{tile.label}</div>
      </a>
    ));

  return (
    <div className={`svd-mobile ${mlFontClass}`}>
      <style>{`
        /* Page ends in the shared dark LandingFooter; match the desktop landing's
           fix so no light bar shows below it on overscroll/rubber-band. */
        body { background-color: #0F1513 !important; }

        .svd-mobile {
          --font-sans: var(--ml-font-sans), system-ui, sans-serif;
          --font-serif: var(--ml-font-serif), Georgia, serif;
          --font-mono: var(--ml-font-mono), ui-monospace, monospace;
          --font-serif-display: var(--ml-font-serif-display), Georgia, serif;

          --green-900:#10211B; --green-800:#16312A; --green-700:#1C5138; --green-600:#2F6B48;
          --brand:#2F6B48; --accent:#F97316; --accent-strong:#C2410C; --on-accent:#FFFFFF;
          --positive:#2F6B48; --logo-amber:#FBA932;
          --sage-50:#F3F5F1; --sage-100:#E7ECE6; --sage-200:#DCE3DD; --sage-700:#355247;
          --text-body:#10211B; --text-muted:#597066; --text-faint:#C7D2CC;
          --border:#C7D2CC; --border-soft:#E7ECE6; --hairline:#DCE3DD;
          --ink-muted:#9FB0A8;
          --tint-peach:#FDE2C8; --tint-teal:#CCFBF1;
          --radius-md:12px; --radius-lg:16px; --radius-xl:20px; --radius-2xl:28px; --radius-pill:999px;
          --shadow-card:0 1px 2px rgba(16,33,27,.04),0 6px 16px rgba(16,33,27,.06);
          --shadow-accent:0 8px 20px rgba(194,65,12,.28);
          --dur-fast:140ms; --ease-out:cubic-bezier(.16,1,.3,1);

          width:100%; margin:0 auto; background:#fff;
          font-family:var(--font-sans); color:var(--text-body);
          overflow-x:hidden; position:relative;
        }
        .svd-mobile *, .svd-mobile *::before, .svd-mobile *::after { box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
        @keyframes svdMenuDrop { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @media (prefers-reduced-motion: reduce) {
          .svd-mobile *, .svd-mobile *::before, .svd-mobile *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
        }

        .svd-tile { background:#fff; display:flex; flex-direction:column; align-items:center; padding:24px 6px 26px; min-height:158px; min-width:0; cursor:pointer; }
        .svd-tile-badge-slot { height:24px; display:flex; align-items:center; justify-content:center; }
        .svd-tile-badge { font-family:var(--font-sans); font-weight:600; font-size:11px; color:var(--green-900); background:var(--sage-100); padding:3px 9px; border-radius:6px; }
        .svd-tile-icon { height:52px; display:flex; align-items:center; justify-content:center; margin-top:6px; }
        .svd-tile-label { font-family:var(--font-sans); font-weight:600; font-size:15.5px; line-height:1.18; color:var(--sage-700); text-align:center; margin-top:14px; max-width:100%; overflow-wrap:break-word; hyphens:auto; }

        .svd-cta-blue { width:100%; display:flex; align-items:center; justify-content:center; gap:8px; font-family:var(--font-sans); font-weight:600; font-size:16px; color:#fff; background:#2056C0; border:none; border-radius:var(--radius-md); padding:15px; cursor:pointer; box-shadow:0 6px 16px rgba(32,86,192,.30); }
        .svd-cta-accent { width:100%; max-width:320px; display:flex; align-items:center; justify-content:center; gap:8px; font-family:var(--font-sans); font-weight:600; font-size:16px; color:var(--on-accent); background:var(--accent); border:none; border-radius:var(--radius-md); padding:15px; cursor:pointer; box-shadow:var(--shadow-accent); margin:0 auto; }
      `}</style>

      {/* ===================== STICKY HEADER ===================== */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 60,
          background: "rgba(255,255,255,0.86)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--hairline)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Image src="/svendetid-icon.png" alt="Svendetid" width={30} height={30} priority style={{ width: 30, height: 30, display: "block", borderRadius: 7 }} />
            <span style={{ fontFamily: "var(--font-serif-display)", fontWeight: 400, fontSize: 22, letterSpacing: "-0.02em", color: "var(--green-900)" }}>
              Svendetid
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontFamily: "var(--font-sans)",
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#FFFFFF",
                  background: "#000000",
                  padding: "8px 14px",
                  borderRadius: "var(--radius-pill)",
                  boxShadow: "none",
                  cursor: "pointer",
                }}
              >
                {t.getStarted}
              </span>
            </SignUpButton>
            <button
              onClick={() => setMenuOpen((s) => !s)}
              aria-label="Menu"
              aria-expanded={menuOpen}
              aria-controls="svd-mobile-nav"
              style={{
                width: 44,
                height: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-pill)",
                background: "#fff",
                cursor: "pointer",
                color: "var(--green-900)",
                padding: 0,
              }}
            >
              <Icon name={menuOpen ? "close" : "menu"} size={20} />
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav
            id="svd-mobile-nav"
            style={{
              borderTop: "1px solid var(--hairline)",
              background: "#fff",
              padding: "8px 18px 16px",
              animation: "svdMenuDrop var(--dur-fast) var(--ease-out)",
            }}
          >
            {/* Features — expandable */}
            <button
              onClick={() => setFeaturesOpen((s) => !s)}
              aria-expanded={featuresOpen}
              aria-controls="svd-mobile-features-list"
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "13px 2px", background: "transparent", border: "none",
                borderBottom: "1px solid var(--border-soft)", cursor: "pointer",
                color: "var(--green-900)", fontSize: 16, fontWeight: 500, fontFamily: "var(--font-sans)",
              }}
            >
              <span>{t.navFeaturesLabel}</span>
              <svg viewBox="0 0 512 512" width={16} height={16} fill="none" stroke="var(--text-faint)" strokeWidth={40} strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: "transform 0.2s", transform: featuresOpen ? "rotate(90deg)" : "rotate(0deg)" }}>
                <polyline points="184 112 328 256 184 400" />
              </svg>
            </button>
            {featuresOpen && (
              <div id="svd-mobile-features-list" style={{ paddingBottom: 4 }}>
                {t.navFeatures.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "11px 12px", textDecoration: "none",
                      color: "var(--green-900)", fontSize: 15, fontWeight: 500,
                      borderBottom: "1px solid var(--border-soft)",
                    }}
                  >
                    <span style={{ fontSize: 18, width: 24, textAlign: "center", lineHeight: 1 }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </a>
                ))}
              </div>
            )}
            {/* Svend AI */}
            <a
              href={t.navSvendAiHref}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "13px 2px", textDecoration: "none",
                color: "var(--green-900)", fontSize: 16, fontWeight: 500,
                borderBottom: "1px solid var(--border-soft)",
              }}
            >
              <span>{t.navSvendAi}</span>
              <svg viewBox="0 0 512 512" width={16} height={16} fill="none" stroke="var(--text-faint)" strokeWidth={40} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="184 112 328 256 184 400" />
              </svg>
            </a>
            {/* Priser */}
            <a
              href={t.navPricingHref}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "13px 2px", textDecoration: "none",
                color: "var(--green-900)", fontSize: 16, fontWeight: 500,
                borderBottom: "1px solid var(--border-soft)",
              }}
            >
              <span>{t.navPricing}</span>
              <svg viewBox="0 0 512 512" width={16} height={16} fill="none" stroke="var(--text-faint)" strokeWidth={40} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="184 112 328 256 184 400" />
              </svg>
            </a>
            <SignInButton mode="modal" fallbackRedirectUrl="/dashboard">
              <button
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "center",
                  marginTop: 14,
                  padding: 13,
                  color: "var(--green-900)",
                  fontWeight: 600,
                  fontSize: 15,
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  background: "#fff",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                }}
              >
                {t.signIn}
              </button>
            </SignInButton>
          </nav>
        )}
      </header>

      {/* ===================== HERO ===================== */}
      <section style={{ padding: "34px 20px 6px", textAlign: "center" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--accent-strong)",
            marginBottom: 16,
          }}
        >
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--accent)" }} />
          {t.heroBadge}
        </div>
        <h1
          style={{
            fontFamily: "var(--font-serif)",
            fontWeight: 700,
            fontSize: 38,
            lineHeight: 1.06,
            letterSpacing: "-0.025em",
            color: "var(--green-900)",
            margin: "0 0 14px",
          }}
        >
          {t.heroH1a}
          <br />
          {t.heroH1b}<span style={{ fontStyle: "italic", fontWeight: 600, color: "var(--brand)" }}>{t.heroH1em}</span>.
        </h1>
        <p
          style={{
            fontSize: 15.5,
            lineHeight: 1.55,
            color: "var(--text-muted)",
            margin: "0 auto 22px",
            maxWidth: 300,
          }}
        >
          {t.heroSub}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 360, margin: "0 auto" }}>
          <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
            <button className="svd-cta-blue">
              <span>{t.heroCta}</span>
              <Icon name="arrow-forward" size={17} color="#fff" />
            </button>
          </SignUpButton>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            marginTop: 14,
            fontSize: 13,
            color: "var(--text-muted)",
          }}
        >
          <Icon name="checkmark-circle" size={14} color="var(--brand)" />
          <span>{t.heroNote}</span>
        </div>
      </section>

      {/* ===================== FEATURE TILES ===================== */}
      <section style={{ padding: "14px 16px 6px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
            background: "var(--hairline)",
            borderTop: "1px solid var(--hairline)",
            borderBottom: "1px solid var(--hairline)",
          }}
        >
          {/* Svend AI tile */}
          <a href={lang === 'en' ? "/en/ai" : "/ai"} className="svd-tile" style={{ textDecoration: "none" }}>
            <div className="svd-tile-badge-slot">
              <span className="svd-tile-badge">{t.svendAiTileBadge}</span>
            </div>
            <div className="svd-tile-icon">
              <Icon name="sparkle" size={34} color="#C2410C" />
            </div>
            <div className="svd-tile-label">Svend AI</div>
          </a>
          <TilesRow items={t.tilesTop} />
          <div style={{ gridColumn: "1 / -1", height: 1, background: "var(--border)" }} />
          <TilesRow items={t.tilesBottom} />
        </div>
      </section>

     

      {/* ===================== FEATURE BLOCKS ===================== */}
      <section style={{ padding: "42px 22px 8px" }}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--accent-strong)",
              marginBottom: 12,
            }}
          >
            {t.featuresEyebrow}
          </div>
          <h2
            style={{
              fontFamily: "var(--font-serif)",
              fontWeight: 700,
              fontSize: 30,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "var(--green-900)",
              margin: 0,
            }}
          >
            {t.featuresH2a}<span style={{ fontStyle: "italic", fontWeight: 600 }}>{t.featuresH2em}</span>{t.featuresH2b}
          </h2>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {t.features.map((f) => (
            <div
              key={f.title}
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-xl)",
                background: "#fff",
                boxShadow: "var(--shadow-card)",
                padding: "18px 18px 20px",
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 9,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: f.tint,
                  marginBottom: 12,
                }}
              >
                <Icon name={f.icon} size={17} color={f.iconColor} />
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--accent-strong)",
                  marginBottom: 8,
                }}
              >
                {f.eyebrow}
              </div>
              <h3
                style={{
                  fontFamily: "var(--font-serif)",
                  fontWeight: 600,
                  fontSize: 20,
                  lineHeight: 1.16,
                  letterSpacing: "-0.015em",
                  color: "var(--green-900)",
                  margin: "0 0 8px",
                }}
              >
                {f.title}
              </h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-muted)", margin: "0 0 12px" }}>{f.body}</p>
              <a
                href={f.href}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: "var(--accent-strong)",
                }}
              >
                {f.link}
                <Icon name="arrow-forward" size={14} color="var(--accent-strong)" />
              </a>
            </div>
          ))}
        </div>
      </section>



      {/* ===================== CLOSING CTA ===================== */}
      <section style={{ padding: "0 22px 44px" }}>
        <div
          style={{
            background: "#10211B",
            borderRadius: "var(--radius-2xl)",
            padding: "40px 26px",
            textAlign: "center",
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -40,
              right: -30,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(249,115,22,0.35), transparent 70%)",
            }}
          />
          <div style={{ position: "relative" }}>
            <Image src="/svendetid-icon.png" alt="" width={46} height={46} style={{ width: 46, height: 46, margin: "0 auto 18px", display: "block", borderRadius: 10 }} />
            <h2
              style={{
                fontFamily: "var(--font-serif)",
                fontWeight: 700,
                fontSize: 30,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                margin: "0 0 12px",
              }}
            >
              {t.ctaH2a}
              <br />
              {t.ctaH2b}
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.55, color: "var(--ink-muted)", margin: "0 auto 22px", maxWidth: 300 }}>
              {t.ctaSub}
            </p>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
              <button className="svd-cta-accent">
                <span>{t.ctaBtn}</span>
                <Icon name="arrow-forward" size={17} color="#fff" />
              </button>
            </SignUpButton>
          </div>
        </div>
      </section>

      <LandingFooter lang={lang} daHref="/" enHref="/en" />
    </div>
  );
}
